import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { POSTransaction } from '../types/pos';
import { printReceiptToPrinter } from '../services/posService';
import { ReceiptPrintView } from '../components/pos/ReceiptPrintView';
import { fetchCompanyLogoDataUrl } from '../hooks/useCompanyLogo';

export type ReceiptPrintResult = { ok: true } | { ok: false; error: string };

export type PosPrintMode = 'browser' | 'thermal';

const PRINT_MODE_STORAGE_KEY = 'stockm_pos_print_mode';

/** Print via the browser dialog — works on any OS with any locally installed printer. */
export function getPosPrintMode(): PosPrintMode {
  try {
    const stored = localStorage.getItem(PRINT_MODE_STORAGE_KEY);
    if (stored === 'thermal') return 'thermal';
  } catch {
    /* private browsing */
  }
  return 'browser';
}

export function setPosPrintMode(mode: PosPrintMode): void {
  try {
    localStorage.setItem(PRINT_MODE_STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
}

/**
 * Receipt layout for iframe / browser print.
 * Sized for 58–80 mm thermal rolls; scales on A4/letter via the browser print dialog.
 */
const RECEIPT_PRINT_STYLES = `
  @page {
    size: auto;
    margin: 3mm;
  }

  * {
    box-sizing: border-box;
  }

  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    color: #000;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  body {
    display: flex;
    justify-content: center;
    font-family: 'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 12px;
    line-height: 1.35;
  }

  .receipt-print-document {
    width: 72mm;
    max-width: 100%;
    padding: 2mm 3mm 4mm;
  }

  .receipt-print-header {
    text-align: center;
    padding-bottom: 8px;
    border-bottom: 1px dashed #000;
    margin-bottom: 8px;
  }

  .receipt-print-logo {
    display: block;
    width: 18mm;
    height: 18mm;
    margin: 0 auto 2mm;
    object-fit: contain;
  }

  .receipt-print-store {
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 4px;
  }

  .receipt-print-badge {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .receipt-print-meta {
    margin-bottom: 8px;
    padding-bottom: 6px;
    border-bottom: 1px dashed #000;
  }

  .receipt-print-meta-row {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    padding: 2px 0;
    font-size: 11px;
  }

  .receipt-print-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
    margin-bottom: 8px;
  }

  .receipt-print-table th {
    text-align: left;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 4px 0;
    border-bottom: 1px solid #000;
  }

  .receipt-print-table th:nth-child(2),
  .receipt-print-table th:nth-child(3) {
    text-align: right;
  }

  .receipt-print-table td {
    padding: 5px 0;
    border-bottom: 1px dotted #ccc;
    vertical-align: top;
  }

  .receipt-print-item-name {
    font-weight: 600;
  }

  .receipt-print-item-code {
    font-size: 9px;
    color: #444;
    margin-top: 1px;
  }

  .receipt-print-qty,
  .receipt-print-amount {
    text-align: right;
    white-space: nowrap;
  }

  .receipt-print-empty {
    text-align: center;
    color: #666;
    padding: 12px 0 !important;
  }

  .receipt-print-summary {
    border-top: 1px dashed #000;
    padding-top: 6px;
    margin-bottom: 8px;
  }

  .receipt-print-summary-row {
    display: flex;
    justify-content: space-between;
    padding: 2px 0;
    font-size: 11px;
  }

  .receipt-print-summary-total {
    margin-top: 4px;
    padding-top: 6px;
    border-top: 1px solid #000;
    font-size: 14px;
    font-weight: 700;
  }

  .receipt-print-payments {
    border-top: 1px dashed #000;
    padding-top: 6px;
    margin-bottom: 8px;
  }

  .receipt-print-payments-title {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin-bottom: 4px;
  }

  .receipt-print-payment-row {
    display: flex;
    justify-content: space-between;
    padding: 2px 0;
    font-size: 11px;
  }

  .receipt-print-footer {
    text-align: center;
    padding-top: 10px;
    font-size: 12px;
    font-weight: 700;
    border-top: 1px dashed #000;
  }
`;

function mountReceiptInIframe(
  transaction: POSTransaction,
  storeName: string,
  logoSrc: string | null,
): { iframe: HTMLIFrameElement; cleanup: () => void } | null {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'Receipt print');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;';

  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  const doc = iframe.contentDocument;
  if (!win || !doc) {
    iframe.remove();
    return null;
  }

  doc.open();
  doc.write('<!DOCTYPE html><html><head><meta charset="utf-8"></head><body></body></html>');
  doc.close();

  const style = doc.createElement('style');
  style.textContent = RECEIPT_PRINT_STYLES;
  doc.head.appendChild(style);

  const mount = doc.createElement('div');
  doc.body.appendChild(mount);

  let root: Root | null = createRoot(mount);
  root.render(
    React.createElement(ReceiptPrintView, {
      transaction,
      storeName,
      className: 'receipt-print-for-output',
      logoSrc,
    }),
  );

  const cleanup = () => {
    try {
      root?.unmount();
    } catch {
      /* ignore */
    }
    root = null;
    iframe.remove();
  };

  return { iframe, cleanup };
}

function openPrintDialog(iframe: HTMLIFrameElement, cleanup: () => void): Promise<ReceiptPrintResult> {
  return new Promise((resolve) => {
    const win = iframe.contentWindow;
    if (!win) {
      cleanup();
      resolve({ ok: false, error: 'Could not open print dialog.' });
      return;
    }

    const finish = (result: ReceiptPrintResult) => {
      cleanup();
      resolve(result);
    };

    win.addEventListener('afterprint', () => finish({ ok: true }), { once: true });

    const runPrint = () => {
      try {
        win.focus();
        win.print();
        window.setTimeout(() => finish({ ok: true }), 2500);
      } catch {
        finish({ ok: false, error: 'Could not open print dialog.' });
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(runPrint);
    });
  });
}

/**
 * Print the exact receipt preview the cashier reviewed (WYSIWYG).
 * The company logo is embedded here so the print iframe always has it.
 */
export async function printReceiptFromPreview(previewElement: HTMLElement): Promise<ReceiptPrintResult> {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'Receipt print');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;';

  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  const doc = iframe.contentDocument;
  if (!win || !doc) {
    iframe.remove();
    return { ok: false, error: 'Could not prepare receipt for printing.' };
  }

  const logo = await fetchCompanyLogoDataUrl();
  const clone = previewElement.cloneNode(true) as HTMLElement;
  if (logo) {
    const header = clone.querySelector('.receipt-print-header');
    if (header) {
      let img = header.querySelector('img.receipt-print-logo') as HTMLImageElement | null;
      if (!img) {
        img = document.createElement('img');
        img.className = 'receipt-print-logo';
        img.alt = 'Company logo';
        header.insertBefore(img, header.firstChild);
      }
      img.src = logo;
    }
  }

  doc.open();
  doc.write('<!DOCTYPE html><html><head><meta charset="utf-8"></head><body></body></html>');
  doc.close();

  const style = doc.createElement('style');
  style.textContent = RECEIPT_PRINT_STYLES;
  doc.head.appendChild(style);

  const mount = doc.createElement('div');
  mount.innerHTML = clone.outerHTML;
  doc.body.appendChild(mount);

  const cleanup = () => {
    iframe.remove();
  };

  return openPrintDialog(iframe, cleanup);
}

/**
 * Open the system print dialog with a receipt formatted for thermal or standard printers.
 */
export async function printReceiptInBrowser(
  transaction: POSTransaction,
  storeName: string,
): Promise<ReceiptPrintResult> {
  const logoSrc = await fetchCompanyLogoDataUrl();
  const mounted = mountReceiptInIframe(transaction, storeName, logoSrc);
  if (!mounted) {
    return Promise.resolve({ ok: false, error: 'Could not prepare receipt for printing.' });
  }

  const { iframe, cleanup } = mounted;
  return openPrintDialog(iframe, cleanup);
}

async function tryThermalPrint(
  transaction: POSTransaction,
  storeName: string,
): Promise<ReceiptPrintResult> {
  try {
    await printReceiptToPrinter(transaction, storeName);
    return { ok: true };
  } catch (err: unknown) {
    const ax = err as { response?: { data?: { detail?: string } }; message?: string };
    const detail = ax?.response?.data?.detail;
    const error =
      typeof detail === 'string'
        ? detail
        : ax?.message || 'Direct thermal print failed.';
    return { ok: false, error };
  }
}

/**
 * Print a receipt after the cashier has reviewed the preview.
 * Uses the on-screen preview when provided so output matches what was checked.
 */
export async function executeReceiptPrint(
  transaction: POSTransaction,
  storeName: string,
  previewElement?: HTMLElement | null,
): Promise<ReceiptPrintResult> {
  const mode = getPosPrintMode();

  if (mode === 'thermal') {
    const thermal = await tryThermalPrint(transaction, storeName);
    if (thermal.ok) return thermal;
  }

  if (previewElement) {
    return printReceiptFromPreview(previewElement);
  }

  return printReceiptInBrowser(transaction, storeName);
}

export type ReceiptPrintRequest = {
  transaction: POSTransaction;
  storeName: string;
};

type ReceiptPrintHandler = (request: ReceiptPrintRequest) => void;

let receiptPrintHandler: ReceiptPrintHandler | null = null;

export function registerReceiptPrintHandler(handler: ReceiptPrintHandler): () => void {
  receiptPrintHandler = handler;
  return () => {
    if (receiptPrintHandler === handler) {
      receiptPrintHandler = null;
    }
  };
}

/** Open receipt preview modal — cashier must review before printing. */
export function printPosReceipt(transaction: POSTransaction, storeName: string): boolean {
  if (!receiptPrintHandler) return false;
  receiptPrintHandler({ transaction, storeName });
  return true;
}
